import { formatDayKey, localDate, type OwnerParticipant, type PackagePause, type ProfilePack, type UpcomingBooking } from "@teamna/shared";
import { useState } from "react";
import { Avatar, Badge, Button, ErrorState, SkeletonList } from "../../components/index.ts";
import { useActiveBusiness, useParticipantProfile } from "@teamna/web-client/hooks/use-owner.ts";
import type { Words } from "../families/model.ts";
import { BookingList, VisitList } from "./children.tsx";
import { EndManyPausesPanel, EndPausePanel, FreezeManyPanel, FreezePanel, PackagePauseNotice } from "./freeze.tsx";
import { HealthDetails } from "./health.tsx";
import { CarryOverPanel, ExtendPanel } from "./package-validity.tsx";
import { AdjustPanel, BookPanel, CancelPanel } from "./panels.tsx";
import { OwnerMembership } from "./membership.tsx";
import { RecordPaymentButton, RecordPaymentPanel } from "./record-payment.tsx";

/**
 * The client page (Pilates studio shape, C1): where clients book for themselves, the Overview of a
 * member is about the client. Each pack is its own row with the sessions left, the end date, the
 * pause and its own Freeze or End the pause (a private pack too: plan decision D17). Over the packs,
 * Freeze all pauses every pack at once (the first action, in its own colour), and End all pauses
 * ends them (D17 owner comment, 2026-10-25). The owner and admins also have Extend and Carry over
 * on each pack (card #1, `package-validity.tsx`). Then the next
 * bookings, the visits, the health answers (C2, `health.tsx`) and the client's own note.
 *
 * The health answers never go in the Clients export.
 */

/** The packs of a client, each on its own row. Plain props, so Storybook draws it. */
export function PackRows({
  packs,
  today,
  onFreeze,
  onEndPause,
  onExtend,
  onCarryOver,
}: {
  packs: ProfilePack[];
  /** Today in the business timezone, `yyyy-MM-dd`. */
  today: string;
  onFreeze?: (pack: ProfilePack) => void;
  onEndPause?: (pack: ProfilePack, pause: PackagePause) => void;
  /** Extend (card #1): shown on a pack with `canExtend`. */
  onExtend?: (pack: ProfilePack) => void;
  /** Carry over (card #1): shown on a pack with `canCarryOver`. */
  onCarryOver?: (pack: ProfilePack) => void;
}) {
  if (packs.length === 0) return <p className="text-[14px] text-ink-3">No package yet.</p>;
  return (
    <ul className="divide-y divide-line rounded-card border border-line" aria-label="Packages">
      {packs.map((pack) => (
        <li key={pack.purchaseId} className="flex flex-col gap-3 px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-ink" dir="auto">
                  {pack.packageName}
                </span>
                {pack.packageKind === "private" ? <Badge tone="quiet">Private</Badge> : null}
                {pack.membership ? <Badge tone="info">Membership month</Badge> : null}
                {pack.intro ? <Badge tone="confirmed">Intro offer</Badge> : null}
                {pack.paymentStatus === "owed" ? <Badge tone="late">Owes</Badge> : null}
                {pack.startsOn ? <Badge tone="info">Next</Badge> : null}
                {pack.state === "used_up" ? <Badge tone="quiet">Used up</Badge> : null}
                {pack.state === "ended" ? <Badge tone="quiet">Ended</Badge> : null}
              </p>
              <p className="text-[13px] text-ink-2">
                {pack.startsOn ? `Starts on ${formatDayKey(pack.startsOn)}. ` : null}
                {pack.state === "ended" ? "Ended on" : "Ends on"} {formatDayKey(pack.endsOn)}
                {pack.state === "ended"
                  ? "."
                  : pack.unlimited
                    ? ". Unlimited."
                    : pack.startsOn
                      ? `. ${pack.sessionsCount} ${pack.sessionsCount === 1 ? "session" : "sessions"}.`
                      : `. ${pack.sessionsLeft} of ${pack.sessionsCount} sessions left.`}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3">
              {pack.state !== "ended" && !pack.unlimited && !pack.startsOn ? (
                <span className="text-[24px] leading-none font-semibold text-accent-strong tabular-nums" aria-hidden="true">
                  {pack.sessionsLeft}
                </span>
              ) : null}
              {/* One pause at a time on the screen: End the pause first, then Freeze again. */}
              {pack.canFreeze && !pack.pause && onFreeze ? (
                <Button variant="secondary" onClick={() => onFreeze(pack)}>
                  Freeze
                </Button>
              ) : null}
              {pack.canExtend && onExtend ? (
                <Button variant="secondary" onClick={() => onExtend(pack)}>
                  Extend
                </Button>
              ) : null}
              {pack.canCarryOver && onCarryOver ? (
                <Button variant="secondary" onClick={() => onCarryOver(pack)}>
                  Carry over
                </Button>
              ) : null}
            </div>
          </div>
          {pack.pause ? (
            <PackagePauseNotice
              pause={pack.pause}
              today={today}
              {...(onEndPause ? { onEnd: () => onEndPause(pack, pack.pause!) } : {})}
            />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** The sessions the client came to, newest first. */
/** The client's Overview: the packs, the actions on the balance, the next bookings, the visits and the note. */
export function ClientCard({
  businessId,
  participant,
  memberName,
  timezone,
  words,
}: {
  businessId: string;
  participant: OwnerParticipant;
  memberName: string;
  timezone: string;
  words: Words;
}) {
  const profile = useParticipantProfile(businessId, participant.id);
  // The front desk (T1) records payments and books here; a change to the balance or a pause is the owner's.
  const { may } = useActiveBusiness();
  const [adjust, setAdjust] = useState<"add" | "remove" | null>(null);
  const [recording, setRecording] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [cancel, setCancel] = useState<UpcomingBooking | null>(null);
  const [freeze, setFreeze] = useState<ProfilePack | null>(null);
  const [endPause, setEndPause] = useState<PackagePause | null>(null);
  const [extend, setExtend] = useState<ProfilePack | null>(null);
  const [carryOver, setCarryOver] = useState<ProfilePack | null>(null);
  // Freeze all and End all pauses (D17): every pack of the client.
  const [freezeAll, setFreezeAll] = useState(false);
  const [endAll, setEndAll] = useState(false);
  const membership = profile.data?.membership ?? null;

  const name = participant.displayName;
  const packs = profile.data?.packs ?? [];
  const sessionsLeft = profile.data ? packs.reduce((total, pack) => total + pack.sessionsLeft, 0) : participant.sessionsLeft;
  const upcoming = profile.data?.upcoming ?? [];
  const today = localDate(new Date(), timezone);
  const note = profile.data?.participant.notes ?? participant.notes ?? null;
  // Freeze all is the first action on the packs; each pack keeps its own Freeze. End all pauses
  // shows when two or more packs are paused (one pause has its own End the pause).
  const freezable = packs.filter((pack) => pack.canFreeze && !pack.pause);
  const paused = packs.filter((pack) => pack.pause);

  return (
    <section aria-label={name} className="rounded-card border border-line bg-paper shadow-card">
      <div className="flex flex-wrap items-center gap-4 border-b border-line px-5 py-4">
        <Avatar name={name} photoUrl={participant.photoUrl} size={56} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 className="text-[21px]" dir="auto">
            {name}
          </h2>
          <p className="text-[14px] text-ink-2">{sessionsLeft === 1 ? "1 session left" : `${sessionsLeft} sessions left`}.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <RecordPaymentButton disabled={!profile.data} refresh={() => profile.refetch()} onClick={() => setRecording(true)} />
          {may("adjustBalance") ? (
            <>
              <Button variant="secondary" onClick={() => setAdjust("add")}>
                Add sessions
              </Button>
              <Button variant="secondary" disabled={sessionsLeft === 0} onClick={() => setAdjust("remove")}>
                Remove sessions
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <div className="grid gap-6 px-5 py-5 lg:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-[16px]">Packages</h3>
            <div className="flex flex-wrap gap-2">
              {paused.length > 1 && may("endManyPauses") ? (
                <Button variant="secondary" onClick={() => setEndAll(true)}>
                  End all pauses
                </Button>
              ) : null}
              {freezable.length > 0 && may("freezeMany") ? (
                <Button variant="freeze" onClick={() => setFreezeAll(true)}>
                  Freeze all
                </Button>
              ) : null}
            </div>
          </div>
          {membership ? (
            <OwnerMembership businessId={businessId} participantId={participant.id} name={name} membership={membership} canCancel={may("cancelMembership")} />
          ) : null}
          {profile.isLoading ? (
            <SkeletonList rows={2} />
          ) : profile.error ? (
            <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />
          ) : (
            <PackRows
              packs={packs}
              today={today}
              {...(may("freezePackage") ? { onFreeze: setFreeze } : {})}
              {...(may("endPackageFreeze") ? { onEndPause: (_: ProfilePack, pause: PackagePause) => setEndPause(pause) } : {})}
              {...(may("extendPackage") ? { onExtend: setExtend } : {})}
              {...(may("carryOverPackage") ? { onCarryOver: setCarryOver } : {})}
            />
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-[16px]">Next bookings</h3>
              <Button variant="secondary" onClick={() => setBookOpen(true)}>
                Book into a session
              </Button>
            </div>
            {profile.isLoading ? <SkeletonList rows={2} /> : <BookingList bookings={upcoming} timezone={timezone} onCancel={setCancel} />}
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="text-[16px]">Visits</h3>
            {profile.isLoading ? <SkeletonList rows={2} /> : <VisitList visits={profile.data?.visits ?? []} timezone={timezone} />}
          </div>
          <HealthDetails businessId={businessId} participantId={participant.id} timezone={timezone} />
          {note ? (
            <div className="flex flex-col gap-1">
              <h3 className="text-[16px]">Note from the {words.member}</h3>
              <p className="text-[14px] whitespace-pre-line text-ink-2" dir="auto">
                {note}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {adjust ? (
        <AdjustPanel
          businessId={businessId}
          participantId={participant.id}
          name={name}
          memberName={memberName}
          direction={adjust}
          current={sessionsLeft}
          onClose={() => setAdjust(null)}
        />
      ) : null}
      {freeze ? (
        <FreezePanel
          businessId={businessId}
          participantId={participant.id}
          purchaseId={freeze.purchaseId}
          name={name}
          packageName={freeze.packageName}
          expiresAt={freeze.expiresAt}
          timezone={timezone}
          onClose={() => setFreeze(null)}
        />
      ) : null}
      {extend ? (
        <ExtendPanel businessId={businessId} participantId={participant.id} name={name} packs={[extend]} onClose={() => setExtend(null)} />
      ) : null}
      {carryOver ? (
        <CarryOverPanel
          businessId={businessId}
          participantId={participant.id}
          name={name}
          packs={[carryOver]}
          timezone={timezone}
          onClose={() => setCarryOver(null)}
        />
      ) : null}
      {freezeAll ? (
        <FreezeManyPanel
          businessId={businessId}
          participantId={participant.id}
          scope="all"
          title={`Freeze all packages of ${name}`}
          name={name}
          packs={freezable}
          timezone={timezone}
          onClose={() => setFreezeAll(false)}
        />
      ) : null}
      {endAll ? (
        <EndManyPausesPanel
          businessId={businessId}
          participantId={participant.id}
          scope="all"
          title={`End all pauses of ${name}?`}
          onClose={() => setEndAll(false)}
        />
      ) : null}
      {endPause ? (
        <EndPausePanel
          businessId={businessId}
          participantId={participant.id}
          pause={endPause}
          name={name}
          timezone={timezone}
          onClose={() => setEndPause(null)}
        />
      ) : null}
      {recording ? (
        <RecordPaymentPanel
          businessId={businessId}
          people={[participant]}
          memberName={memberName}
          owed={profile.data?.owed ?? []}
          renewal={membership?.renewal ? { participantId: participant.id, name, membership } : null}
          timezone={timezone}
          onClose={() => setRecording(false)}
        />
      ) : null}
      {bookOpen ? (
        <BookPanel
          businessId={businessId}
          participant={participant}
          bookedSessionIds={upcoming.map((row) => row.sessionId)}
          timezone={timezone}
          onClose={() => setBookOpen(false)}
        />
      ) : null}
      {cancel ? (
        <CancelPanel
          businessId={businessId}
          booking={cancel}
          name={name}
          memberName={memberName}
          timezone={timezone}
          onClose={() => setCancel(null)}
        />
      ) : null}
    </section>
  );
}
